import { useEffect } from 'react'
import { motion, useMotionValue, animate, type PanInfo } from 'framer-motion'
import { Check, Trash2 } from 'lucide-react'

type Props = {
  id:             string
  name:           string
  qtyLabel?:      string | null
  isLowStock?:    boolean
  checked:        boolean
  isLast?:        boolean
  checkedByName?: string | null
  notes?:         string | null
  onToggle:       (id: string) => void
  onOpenDetail?:  () => void
  onRemove?:      () => void
  swipedOpen?:    boolean
  onSwipeChange?: (open: boolean) => void
}

const REVEAL_WIDTH     = 80   // px revealed when swiped open
const REVEAL_THRESHOLD = 40   // px past which we snap open instead of closed

export function ShoppingListItemRow({
  id,
  name,
  qtyLabel,
  isLowStock,
  checked,
  isLast,
  checkedByName,
  notes,
  onToggle,
  onOpenDetail,
  onRemove,
  swipedOpen,
  onSwipeChange,
}: Props) {
  const x              = useMotionValue(0)
  const swipeEnabled   = typeof onRemove === 'function'
  const isOpen         = !!swipedOpen

  // Snap to the coordinated open/closed target whenever the parent decides
  useEffect(() => {
    const target = isOpen ? -REVEAL_WIDTH : 0
    if (x.get() !== target) {
      animate(x, target, { type: 'spring', stiffness: 500, damping: 40 })
    }
  }, [isOpen, x])

  function handleDragEnd(_: PointerEvent, info: PanInfo) {
    if (!onSwipeChange) return
    onSwipeChange(info.offset.x < -REVEAL_THRESHOLD)
  }

  function handleCircleClick(e: React.MouseEvent) {
    e.stopPropagation()
    if (isOpen && onSwipeChange) { onSwipeChange(false); return }
    onToggle(id)
  }

  function handleNameClick(e: React.MouseEvent) {
    e.stopPropagation()
    if (isOpen && onSwipeChange) { onSwipeChange(false); return }
    if (onOpenDetail) onOpenDetail()
    else onToggle(id)  // fallback preserves legacy behavior
  }

  function handleRemoveClick(e: React.MouseEvent) {
    e.stopPropagation()
    if (onSwipeChange) onSwipeChange(false)
    if (onRemove) onRemove()
  }

  const subtitleParts: string[] = []
  if (isLowStock && !checked)   subtitleParts.push('Running out')
  if (qtyLabel)                 subtitleParts.push(qtyLabel)
  if (notes)                    subtitleParts.push(notes)
  if (checked && checkedByName) subtitleParts.push(`by ${checkedByName}`)
  const subtitle = subtitleParts.join(' · ')

  const rowTint = checked
    ? 'bg-[#F4F9F5]'
    : isLowStock
    ? 'bg-cream-warm'
    : 'bg-paper'

  return (
    <div
      className={`relative overflow-hidden bg-paper ${!isLast ? 'border-b border-border-hairline' : ''}`}
    >
      {/* Underlay — red Remove button. Sits at the right edge, behind the row (z-0). */}
      {swipeEnabled && (
        <button
          type="button"
          onClick={handleRemoveClick}
          aria-label={`Remove ${name}`}
          tabIndex={isOpen ? 0 : -1}
          aria-hidden={!isOpen}
          className="absolute inset-y-0 right-0 w-[80px] bg-critical text-cream flex flex-col items-center justify-center gap-1 z-0"
        >
          <Trash2 className="h-[14px] w-[14px]" strokeWidth={1.8} />
          <span className="text-[11px] font-semibold tracking-[-0.01em]">Remove</span>
        </button>
      )}

      {/* Foreground — drags left to reveal the underlay. Must be full-width + opaque + above underlay. */}
      <motion.div
        style={{ x }}
        drag={swipeEnabled ? 'x' : false}
        dragConstraints={{ left: -REVEAL_WIDTH, right: 0 }}
        dragElastic={0.08}
        dragMomentum={false}
        onDragEnd={handleDragEnd}
        className={`relative z-10 w-full flex items-center gap-3 px-[14px] py-3 touch-pan-y ${rowTint}`}
      >
        {/* Circle — toggle check/uncheck */}
        <button
          type="button"
          onClick={handleCircleClick}
          aria-label={checked ? `Uncheck ${name}` : `Check off ${name}`}
          className={
            checked
              ? 'flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-forest'
              : isLowStock
              ? 'h-6 w-6 flex-shrink-0 rounded-full border-[1.5px] border-terracotta'
              : 'h-6 w-6 flex-shrink-0 rounded-full border-[1.5px] border-[#D4C4A8]'
          }
        >
          {checked && <Check className="h-3 w-3 text-cream" strokeWidth={2.2} />}
        </button>

        {/* Name + subtitle — open detail sheet */}
        <button
          type="button"
          onClick={handleNameClick}
          aria-label={onOpenDetail ? `Open ${name} options` : undefined}
          className="flex-1 min-w-0 text-left"
        >
          <div
            className={
              checked
                ? 'truncate font-serif text-[15px] font-medium tracking-[-0.01em] text-ink line-through decoration-ink-muted'
                : 'truncate font-serif text-[15px] font-medium tracking-[-0.01em] text-ink'
            }
          >
            {name}
          </div>
          {subtitle && (
            <div
              className={
                isLowStock && !checked
                  ? 'mt-0.5 truncate text-[11px] italic text-terracotta'
                  : checked
                  ? 'mt-0.5 truncate text-[11px] italic text-forest'
                  : 'mt-0.5 truncate text-[11px] italic text-ink-muted'
              }
            >
              {subtitle}
            </div>
          )}
        </button>

        {isLowStock && !checked && (
          <span className="h-2 w-2 rounded-full bg-terracotta flex-shrink-0" />
        )}
      </motion.div>
    </div>
  )
}
